import { useId, useState } from "react";
import { Button } from "../../ui/button";
import { cn } from "../../ui/cn";
import { CheckboxField, SelectField, TextField } from "../../ui/fields";
import { Icon } from "../../ui/icon";
import { filterPanel } from "../components/styles";
import {
  type CatalogFilterValues,
  emptyFilterValues,
  filterValuesToSearchParams,
  searchParamsToQuery,
} from "../filters";
import {
  fulfilmentOptions,
  itemTypeOptions,
  pricingModelOptions,
  sortOptions,
  sourceKindOptions,
  statusOptions,
  verificationOptions,
} from "../labels";
import type { FieldErrors } from "../validation";

type CatalogFiltersProps = Readonly<{
  /** The filters in the page address. The form starts from them. */
  initial: CatalogFilterValues;
  onApply: (values: CatalogFilterValues) => void;
}>;

const ADVANCED: readonly (keyof CatalogFilterValues)[] = [
  "verification",
  "pricingModel",
  "fulfilmentMode",
  "sourceKind",
  "sourceSystem",
  "countryCode",
  "locality",
  "featured",
];

/**
 * The list filters. Applying them writes the page address, and the list reads the address. The form
 * checks the values with the same schema the API uses: a value the API would refuse is flagged here.
 */
export function CatalogFilters({ initial, onApply }: CatalogFiltersProps) {
  const [values, setValues] = useState<CatalogFilterValues>(initial);
  const [errors, setErrors] = useState<FieldErrors>({});
  const set = <K extends keyof CatalogFilterValues>(key: K, value: CatalogFilterValues[K]) =>
    setValues((current) => ({ ...current, [key]: value }));

  const moreId = useId();
  const advancedActive = ADVANCED.some((key) => {
    const value = initial[key];
    return Array.isArray(value) ? value.length > 0 : Boolean(value);
  });

  const [showMore, setShowMore] = useState(advancedActive);

  function apply() {
    const { ignored } = searchParamsToQuery(filterValuesToSearchParams(values));
    if (ignored.length > 0) {
      const next: Record<string, string> = {};
      for (const key of ignored) next[key] = "This value is not valid.";
      setErrors(next);
      return;
    }
    setErrors({});
    onApply(values);
  }

  function clear() {
    setErrors({});
    setValues(emptyFilterValues);
    onApply(emptyFilterValues);
  }

  return (
    <form
      className={filterPanel}
      aria-label="Filter the catalog"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        apply();
      }}
    >
      <div className="grid gap-4 [grid-template-columns:repeat(auto-fit,minmax(min(100%,14rem),1fr))] lg:grid-cols-[minmax(0,1fr)_15rem_15rem]">
        <TextField
          id="filter-q"
          label="Search by name or slug"
          type="search"
          value={values.q}
          onChange={(value) => set("q", value)}
          error={errors.q}
          hint="Finds names and slugs that start with these letters."
          maxLength={100}
        />
        <SelectField
          id="filter-type"
          label="Type"
          value={values.type}
          onChange={(value) => set("type", value)}
          options={itemTypeOptions}
          placeholder="Any type"
          error={errors.type}
        />
        <SelectField
          id="filter-sort"
          label="Sort by"
          value={values.sort}
          onChange={(value) => set("sort", value)}
          options={sortOptions}
          placeholder="Default order"
          error={errors.sort}
        />
      </div>

      <fieldset className="m-0 flex min-w-0 flex-wrap items-center gap-x-6 gap-y-1 border-0 p-0 compact:gap-y-2">
        <legend className="sr-only">Status</legend>
        <span aria-hidden="true" className="w-12 text-[13px] font-semibold">
          Status
        </span>
        {statusOptions.map((option) => (
          <CheckboxField
            key={option.value}
            id={`filter-status-${option.value}`}
            label={option.label}
            checked={values.status.includes(option.value)}
            onChange={(checked) =>
              set(
                "status",
                checked
                  ? [...values.status, option.value]
                  : values.status.filter((status) => status !== option.value),
              )
            }
          />
        ))}
      </fieldset>

      <div
        id={moreId}
        hidden={!showMore}
        className="grid gap-4 [grid-template-columns:repeat(auto-fit,minmax(min(100%,15rem),1fr))]"
      >
        <SelectField
          id="filter-verification"
          label="Verification"
          value={values.verification}
          onChange={(value) => set("verification", value)}
          options={verificationOptions}
          placeholder="Any"
          error={errors.verification}
        />
        <SelectField
          id="filter-pricing-model"
          label="Pricing model"
          value={values.pricingModel}
          onChange={(value) => set("pricingModel", value)}
          options={pricingModelOptions}
          placeholder="Any"
          error={errors.pricingModel}
        />
        <SelectField
          id="filter-fulfilment"
          label="Fulfilment mode"
          value={values.fulfilmentMode}
          onChange={(value) => set("fulfilmentMode", value)}
          options={fulfilmentOptions}
          placeholder="Any"
          error={errors.fulfilmentMode}
        />
        <SelectField
          id="filter-source-kind"
          label="Source"
          value={values.sourceKind}
          onChange={(value) => set("sourceKind", value)}
          options={sourceKindOptions}
          placeholder="Any"
          error={errors.sourceKind}
        />
        <TextField
          id="filter-source-system"
          label="Import source system"
          value={values.sourceSystem}
          onChange={(value) => set("sourceSystem", value)}
          error={errors.sourceSystem}
          hint="Lowercase letters, numbers and underscores."
        />
        <TextField
          id="filter-country"
          label="Country code"
          value={values.countryCode}
          onChange={(value) => set("countryCode", value)}
          error={errors.countryCode}
          maxLength={2}
          hint="Two letters, for example ES."
        />
        <TextField
          id="filter-locality"
          label="Locality"
          value={values.locality}
          onChange={(value) => set("locality", value)}
          error={errors.locality}
        />
        <CheckboxField
          id="filter-featured"
          label="Featured only"
          checked={values.featured}
          onChange={(checked) => set("featured", checked)}
          hint="Items with an editorial priority above 0."
        />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <Button
          type="button"
          variant="ghost"
          aria-expanded={showMore}
          aria-controls={moreId}
          onClick={() => setShowMore((open) => !open)}
          className="-ml-3 font-semibold"
        >
          <Icon
            name="arrow-right-01-round"
            className={cn("size-4 transition-transform", showMore && "rotate-90")}
          />
          More filters
        </Button>
        <div className="flex flex-wrap gap-2.5">
          <Button type="submit">Apply filters</Button>
          <Button type="button" variant="secondary" onClick={clear}>
            Clear filters
          </Button>
        </div>
      </div>
    </form>
  );
}
